import React from "react";
import { Box, Column, Columns, Heading, Stack, Text } from "braid-design-system";
import Loading from "../components/Loading";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";
import { BomIngredientMasterItem, BomIngredientRow, BomMenuItem } from "../types/bom";
import { getAllItems, ItemResponse } from "../services/itemService";
import { getAllBoms, createBom, updateBom, BomDetailLineInput, RestaurantBomResponse } from "../services/bomService";
import { getAllItemResources, ItemResourceResponse } from "../services/itemResourceService";
import { toStaticResourceUrl } from "../utils/resourceUrl";
import BomMenuItemList from "../components/BOM/BomMenuItemList";
import BomDetailPanel from "../components/BOM/BomDetailPanel";
import BomMappingView from "../components/BOM/BomMappingView";
import AddEditIngredientDrawer from "../components/BOM/AddEditIngredientDrawer";

const ITEM_PAGE_SIZE = 100;
const MAX_ITEM_PAGES = 20; // safety bound (2000 items)

const fetchAllItems = async (): Promise<ItemResponse[]> => {
    const all: ItemResponse[] = [];
    for (let page = 1; page <= MAX_ITEM_PAGES; page += 1) {
        const batch = await getAllItems(page, ITEM_PAGE_SIZE, {});
        all.push(...batch);
        if (batch.length < ITEM_PAGE_SIZE) break;
    }
    return all;
};

// One primary (or first, by sort order) image URL per item code, sourced
// from pos_item_resources - no per-item image means no entry in the map.
const buildPrimaryImageMap = (resources: ItemResourceResponse[]): Map<string, string> => {
    const byItemCode = new Map<string, ItemResourceResponse[]>();
    resources
        .filter((r) => r.resource_type === "image" && r.resource_itemlots_code)
        .forEach((r) => {
            const code = r.resource_itemlots_code as string;
            const list = byItemCode.get(code) || [];
            list.push(r);
            byItemCode.set(code, list);
        });

    const result = new Map<string, string>();
    byItemCode.forEach((list, code) => {
        const sorted = [...list].sort((a, b) => (a.resource_sort_order ?? Infinity) - (b.resource_sort_order ?? Infinity));
        const chosen = sorted.find((r) => r.is_primary === 1) || sorted[0];
        const url = toStaticResourceUrl(chosen.resource_path);
        if (url) result.set(code, url);
    });
    return result;
};

const toIngredientRow = (detail: RestaurantBomResponse["details"][number]): BomIngredientRow => ({
    row_id: String(detail.bom_detail_id ?? `${detail.component_code}-${detail.line_no}`),
    ingredient_code: detail.component_code,
    ingredient_name: detail.component_code,
    uom: detail.uom,
    qty_per_1: Number(detail.qty),
    wastage_pct: Number(detail.wastage_percent ?? 0),
    unit_cost: Number(detail.unit_cost ?? 0),
});

const buildMenuItems = (
    items: ItemResponse[],
    activeBoms: RestaurantBomResponse[],
    ingredientNameByCode: Map<string, string>,
    imageByCode: Map<string, string>,
): BomMenuItem[] => {
    const bomByItemCode = new Map(activeBoms.map((bom) => [bom.item_code || "", bom]));

    return items
        .filter((item) => item.item_calreceipe && item.item_code)
        .map((item) => {
            const bom = bomByItemCode.get(item.item_code as string);
            const ingredients = (bom?.details || []).map((detail) => {
                const row = toIngredientRow(detail);
                return { ...row, ingredient_name: ingredientNameByCode.get(row.ingredient_code) || row.ingredient_code };
            });

            return {
                item_code: item.item_code as string,
                item_name: item.item_name || (item.item_code as string),
                item_type: "FINISHED" as const,
                uom: item.item_uom || "PCS",
                image_url: imageByCode.get(item.item_code as string) || null,
                yield_qty: bom?.yield_qty ? Number(bom.yield_qty) : 1,
                version: bom?.version_no || 1,
                bom_id: bom?.bom_id,
                ingredients,
            };
        })
        .sort((a, b) => a.item_name.localeCompare(b.item_name));
};

const buildIngredientMaster = (items: ItemResponse[]): BomIngredientMasterItem[] =>
    items
        .filter((item) => item.item_rawitem && item.item_code)
        .map((item) => ({
            ingredient_code: item.item_code as string,
            ingredient_name: item.item_name || (item.item_code as string),
            uom: item.item_uom || "PCS",
            unit_cost: Number(item.item_cost ?? item.item_avgcost ?? 0),
        }))
        .sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name));

const BOMManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();

    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);

    const [menuItems, setMenuItems] = React.useState<BomMenuItem[]>([]);
    const [ingredientMasterList, setIngredientMasterList] = React.useState<BomIngredientMasterItem[]>([]);
    const [selectedItemCode, setSelectedItemCode] = React.useState<string | null>(null);
    const [searchTerm, setSearchTerm] = React.useState("");

    const selectedItem = React.useMemo(
        () => menuItems.find((item) => item.item_code === selectedItemCode) || null,
        [menuItems, selectedItemCode],
    );

    const [draftIngredients, setDraftIngredients] = React.useState<BomIngredientRow[]>([]);
    const [draftVersion, setDraftVersion] = React.useState<number>(1);
    const [isDirty, setIsDirty] = React.useState(false);

    const [ingredientDrawerOpen, setIngredientDrawerOpen] = React.useState(false);
    const [editingIngredient, setEditingIngredient] = React.useState<BomIngredientRow | undefined>(undefined);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [items, activeBoms, resources] = await Promise.all([
                fetchAllItems(),
                getAllBoms({ status: 1 }),
                getAllItemResources(),
            ]);
            const activeItems = items.filter((item) => item.item_active !== false);
            const imageByCode = buildPrimaryImageMap(resources);
            const ingredientMaster = buildIngredientMaster(activeItems);
            const ingredientNameByCode = new Map(ingredientMaster.map((ing) => [ing.ingredient_code, ing.ingredient_name]));
            const nextMenuItems = buildMenuItems(activeItems, activeBoms, ingredientNameByCode, imageByCode);

            setIngredientMasterList(ingredientMaster);
            setMenuItems(nextMenuItems);
            setSelectedItemCode((prev) => prev && nextMenuItems.some((i) => i.item_code === prev) ? prev : nextMenuItems[0]?.item_code || null);
        } catch (error: any) {
            setLoadError(error?.message || "Failed to load BOM data");
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    React.useEffect(() => {
        setDraftIngredients(selectedItem?.ingredients || []);
        setDraftVersion(selectedItem?.version || 1);
        setIsDirty(false);
    }, [selectedItem]);

    const handleSelectItem = React.useCallback((itemCode: string) => {
        setSelectedItemCode(itemCode);
    }, []);

    const handleAddIngredient = React.useCallback(() => {
        setEditingIngredient(undefined);
        setIngredientDrawerOpen(true);
    }, []);

    const handleEditIngredient = React.useCallback((row: BomIngredientRow) => {
        setEditingIngredient(row);
        setIngredientDrawerOpen(true);
    }, []);

    const handleSaveIngredient = React.useCallback((row: BomIngredientRow) => {
        setDraftIngredients((prev) => {
            const exists = prev.some((r) => r.row_id === row.row_id);
            return exists ? prev.map((r) => (r.row_id === row.row_id ? row : r)) : [...prev, row];
        });
        setIsDirty(true);
    }, []);

    const handleDeleteIngredient = React.useCallback((rowId: string) => {
        setDraftIngredients((prev) => prev.filter((r) => r.row_id !== rowId));
        setIsDirty(true);
    }, []);

    const handleCancel = React.useCallback(() => {
        setDraftIngredients(selectedItem?.ingredients || []);
        setDraftVersion(selectedItem?.version || 1);
        setIsDirty(false);
    }, [selectedItem]);

    const handleDuplicate = React.useCallback(() => {
        if (!selectedItem) return;
        setDraftVersion(selectedItem.version + 1);
        setIsDirty(true);
        toast.showSuccess(`Saving now will create Version ${selectedItem.version + 1} of this BOM.`);
    }, [selectedItem, toast]);

    const handleSave = React.useCallback(async () => {
        if (!selectedItem) return;

        if (draftIngredients.length === 0) {
            toast.showError("Add at least one ingredient before saving the BOM.");
            return;
        }

        const details: BomDetailLineInput[] = draftIngredients.map((row, index) => ({
            component_code: row.ingredient_code,
            component_type: "RAW",
            qty: row.qty_per_1,
            uom: row.uom,
            wastage_percent: row.wastage_pct,
            unit_cost: row.unit_cost,
            line_no: index + 1,
        }));

        const payload = {
            bom_name: selectedItem.item_name,
            yield_qty: selectedItem.yield_qty,
            yield_uom: selectedItem.uom,
            details,
        };

        setSaving(true);
        try {
            const saved = selectedItem.bom_id
                ? await updateBom(selectedItem.bom_id, payload)
                : await createBom({ ...payload, item_code: selectedItem.item_code });

            const ingredientNameByCode = new Map(ingredientMasterList.map((ing) => [ing.ingredient_code, ing.ingredient_name]));
            const savedIngredients = saved.details.map((detail) => {
                const row = toIngredientRow(detail);
                return { ...row, ingredient_name: ingredientNameByCode.get(row.ingredient_code) || row.ingredient_code };
            });

            setMenuItems((prev) =>
                prev.map((item) =>
                    item.item_code === selectedItem.item_code
                        ? { ...item, bom_id: saved.bom_id, version: saved.version_no || item.version, ingredients: savedIngredients }
                        : item,
                ),
            );
            setIsDirty(false);
            toast.showSuccess(`BOM saved for ${selectedItem.item_name} (Version ${saved.version_no}).`);
        } catch (error: any) {
            toast.showError(error?.message || "Failed to save BOM");
        } finally {
            setSaving(false);
        }
    }, [selectedItem, draftIngredients, ingredientMasterList, toast]);

    if (loading) {
        return <Loading message="Please wait, loading BOM data..." />;
    }

    if (loadError) {
        return (
            <Box padding="large">
                <Text tone="critical">{loadError}</Text>
            </Box>
        );
    }

    return (
        <Box padding="medium">
            <Stack space="large">
                <Box display="flex" alignItems="center" gap="small">
                    <Stack space="none">
                        <Heading level="2">BOM Management</Heading>
                    </Stack>
                </Box>

                <Columns space="large" collapseBelow="tablet">
                    <Column width="1/3">
                        <BomMenuItemList
                            items={menuItems}
                            selectedItemCode={selectedItemCode}
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            onSelectItem={handleSelectItem}
                        />
                    </Column>
                    <Column width="2/3">
                        {selectedItem ? (
                            <BomDetailPanel
                                item={selectedItem}
                                draftIngredients={draftIngredients}
                                draftVersion={draftVersion}
                                isDirty={isDirty}
                                canEdit={canManageMasters && !saving}
                                onAddIngredient={handleAddIngredient}
                                onEditIngredient={handleEditIngredient}
                                onDeleteIngredient={handleDeleteIngredient}
                                onCancel={handleCancel}
                                onDuplicate={handleDuplicate}
                                onSave={handleSave}
                            />
                        ) : (
                            <Box background="neutralLight" borderRadius="standard" padding="large">
                                <Text tone="secondary" align="center">
                                    {menuItems.length === 0
                                        ? 'No items are flagged "Calculate Recipe" yet. Enable it on an item to manage its BOM here.'
                                        : "Select a menu item from the list to view or edit its BOM."}
                                </Text>
                            </Box>
                        )}
                    </Column>
                </Columns>

                {selectedItem ? <BomMappingView item={selectedItem} ingredients={draftIngredients} /> : null}
            </Stack>

            <AddEditIngredientDrawer
                isOpen={ingredientDrawerOpen}
                onClose={() => setIngredientDrawerOpen(false)}
                ingredientMaster={ingredientMasterList}
                existingIngredientCodes={draftIngredients.map((r) => r.ingredient_code)}
                ingredientToEdit={editingIngredient}
                onSave={handleSaveIngredient}
            />
        </Box>
    );
};

export default BOMManagement;
